// Plain-words formatting shared by the Leah-facing pages.
import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Cents in the database → "$1,234.56". Negative stays negative. */
export function dollars(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return '—';
  return usd.format(cents / 100);
}

/** "$1,234.56" or "" typed by a person → cents. Returns null when unreadable. */
export function toCents(input: string): number | null {
  const clean = input.replace(/[^0-9.-]/g, '');
  if (!clean) return null;
  const n = Number(clean);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function parse(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : parseISO(value);
  return isValid(d) ? d : null;
}

/** "Sep 16, 2026" */
export function day(value: string | Date | null | undefined): string {
  const d = parse(value);
  return d ? format(d, 'MMM d, yyyy') : '—';
}

/** "September 2026" for a first-of-month date. */
export function monthName(value: string | Date | null | undefined): string {
  const d = parse(value);
  return d ? format(d, 'MMMM yyyy') : '—';
}

/** "3 days ago" / "in 12 days" */
export function ago(value: string | Date | null | undefined): string {
  const d = parse(value);
  if (!d) return 'never';
  const dist = formatDistanceToNowStrict(d);
  return d.getTime() > Date.now() ? `in ${dist}` : `${dist} ago`;
}

/** "2026-09" for today or a given date. */
export function monthKey(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "2026-09" → "2026-09-01" (the database key for books_months). */
export function monthFirst(key: string): string {
  return `${key}-01`;
}

export function roleWords(role: string | null | undefined): string {
  switch (role) {
    case 'owner':
    case 'educator':
      return 'Mentor';
    case 'apprentice':
      return 'Apprentice';
    case 'stylist':
      return 'Licensed stylist';
    default:
      return role ? role.replace(/_/g, ' ') : 'No role yet';
  }
}

export function planWords(entitlement: string | null | undefined): string {
  switch (entitlement) {
    case 'academy':
      return 'Academy';
    case 'academy_plus':
      return 'Academy Plus';
    case 'study':
      return 'Study';
    default:
      return entitlement ?? '—';
  }
}

export function paperworkWords(status: string | null | undefined): string {
  switch (status) {
    case 'filed':
      return 'Paperwork filed';
    case 'not_filed':
      return 'Paperwork not filed';
    case 'unsure':
      return 'Not sure if paperwork is filed';
    default:
      return 'Paperwork question not answered yet';
  }
}

export function subStatusWords(status: string | null | undefined): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'grace':
      return 'Payment problem, still has access';
    case 'cancelled':
      return 'Canceled';
    case 'expired':
      return 'Ended';
    default:
      return status ?? '—';
  }
}

// ── Books (LLC ledger) helpers ───────────────────────────────────────────────

/** "Sep 16, 2026, 3:04 PM" — for closed-at, granted-at, last-entry stamps. */
export function dateTime(value: string | Date | null | undefined): string {
  const d = parse(value);
  return d ? format(d, 'MMM d, yyyy, h:mm a') : '—';
}

/** 51 → "51%". Keeps up to two decimals when the share is not whole. */
export function pct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return `${Number.isInteger(v) ? v : v.toFixed(2).replace(/\.?0+$/, '')}%`;
}

/** "2026-09-01" → "2026-09" (the key the Apple pages and llc_overview use). */
export function monthKeyOf(first: string | null | undefined): string {
  return first ? first.slice(0, 7) : '';
}

/** 'leah' → "Leah", 'laura' → "Laura", anything else title-cased. */
export function memberWords(member: string | null | undefined): string {
  if (!member) return '—';
  return member.charAt(0).toUpperCase() + member.slice(1);
}

/** "2026-09-01" → "2026-08-01" (the first of the month before). */
export function previousMonthFirst(first: string): string {
  const [y, m] = first.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}
